import { useEffect, useState, useMemo } from "react";

/**
 * Circular feasibility ring. Renders total_eligible / target as a filled arc,
 * colour-coded by supply health, with a micro-pulse animation each time the
 * feasibility number changes (so the wizard visually reacts to every chip
 * toggle in the pillars above).
 *
 * Thresholds (ratio = eligible / target):
 *   ratio < 1     → red    (INSUFFICIENT — study can't fill)
 *   1 ≤ ratio < 3 → amber  (TIGHT — thin margin)
 *   3 ≤ ratio ≤ 10 → green (HEALTHY)
 *   ratio > 10    → blue   (OVERSUPPLIED — consider tighter targeting)
 *
 * The arc fill is log-scaled so the visual range (1x → 500x) doesn't collapse
 * into a permanent 100% fill for healthy studies.
 */
const R = 68;                    // ring radius (px)
const STROKE = 10;
const SIZE = (R + STROKE) * 2;
const CIRC = 2 * Math.PI * R;

function health(ratio) {
  if (!isFinite(ratio) || ratio <= 0) return {
    key: "empty", label: "—", color: "var(--rl-text-secondary)",
    whisper: "Set a target and audience filters to see supply health.",
  };
  if (ratio < 1) return {
    key: "insufficient", label: "Insufficient", color: "var(--rl-accent)",
    whisper: "Your filters have shrunk the pool below the target. Loosen a pillar filter or widen the geography.",
  };
  if (ratio < 3) return {
    key: "tight", label: "Tight", color: "#F5A524",
    whisper: "Feasible but thin — expect a longer field time. Consider broadening one bracket for a safety margin.",
  };
  if (ratio <= 10) return {
    key: "healthy", label: "Healthy", color: "var(--rl-success)",
    whisper: "Well-sized pool — the study should fill on schedule with room for quality filtering.",
  };
  return {
    key: "oversupplied", label: "Oversupplied", color: "var(--rl-brand)",
    whisper: "Very large pool — consider tighter targeting to reach a higher-fit, more representative sample.",
  };
}

// Log-scaled 0..1 fill. 1x = 25%, 3x = 50%, 10x = 75%, 30x = 90%, 100x+ = 100%.
function fillFraction(ratio) {
  if (!isFinite(ratio) || ratio <= 0) return 0;
  const capped = Math.min(ratio, 100);
  return Math.max(0.04, Math.log10(capped * 10) / 3);  // log10(10)/3 = .33 at 1x → shift so 1x ≈ 0.33
}

function fmt(n) {
  if (n === null || n === undefined) return "—";
  if (n < 1000) return String(Math.round(n));
  if (n < 1000000) return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}K`;
  return `${(n / 1000000).toFixed(1)}M`;
}

export default function FeasibilityRing({ eligible, target, loading, testid = "feasibility-ring" }) {
  const ratio = target > 0 ? (eligible || 0) / target : 0;
  const meta = useMemo(() => health(ratio), [ratio]);
  const fill = useMemo(() => fillFraction(ratio), [ratio]);
  const dashOffset = CIRC * (1 - fill);
  const [whisperOpen, setWhisperOpen] = useState(false);

  // Pulse animation trigger — bump a nonce every time `eligible` changes so
  // the ring re-runs the entrance animation for that fresh number.
  const [nonce, setNonce] = useState(0);
  useEffect(() => { setNonce((n) => n + 1); }, [eligible, target]);

  return (
    <div
      className="relative flex flex-col items-center select-none"
      data-testid={testid}
      data-health={meta.key}
      onMouseEnter={() => setWhisperOpen(true)}
      onMouseLeave={() => setWhisperOpen(false)}
      onFocus={() => setWhisperOpen(true)}
      onBlur={() => setWhisperOpen(false)}
      tabIndex={0}
      aria-describedby={`${testid}-whisper`}
    >
      {/* Ring box — nested `relative` container sized exactly to the SVG so
          the absolute-positioned label centers ONLY on the ring (not on
          the whole card including the badge / whisper below it). */}
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="block"
          aria-hidden="true"
        >
          {/* Track */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            fill="none"
            stroke="var(--rl-border)"
            strokeWidth={STROKE}
          />
          {/* Progress arc — rotated so it starts at 12 o'clock. Uses `key={nonce}`
              so React re-mounts it for a natural CSS entrance every time the
              underlying number changes. */}
          <circle
            key={nonce}
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            fill="none"
            stroke={meta.color}
            strokeWidth={STROKE}
            strokeLinecap="butt"
            strokeDasharray={CIRC}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            style={{
              transition: "stroke-dashoffset 700ms cubic-bezier(.16,1,.3,1), stroke 300ms linear",
            }}
          />
          {/* Tick marks at 1x, 3x, 10x thresholds for legibility */}
          {[0.33, 0.55, 0.75].map((f, i) => {
            const angle = -Math.PI / 2 + 2 * Math.PI * f;
            const cx1 = SIZE / 2 + (R - STROKE) * Math.cos(angle);
            const cy1 = SIZE / 2 + (R - STROKE) * Math.sin(angle);
            const cx2 = SIZE / 2 + (R + STROKE + 2) * Math.cos(angle);
            const cy2 = SIZE / 2 + (R + STROKE + 2) * Math.sin(angle);
            return (
              <line
                key={i}
                x1={cx1} y1={cy1} x2={cx2} y2={cy2}
                stroke="var(--rl-text-secondary)"
                strokeWidth={1}
                opacity={0.35}
              />
            );
          })}
        </svg>
        {/* Center label — pinned to the geometric center of the SVG only. */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
          style={{ padding: STROKE + 4 }}
        >
          <div className="text-[9px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)] leading-none">Eligible</div>
          <div
            className="font-display font-black text-[30px] leading-none tabular-nums text-[var(--rl-text)] mt-1"
            data-testid={`${testid}-value`}
          >
            {loading && (eligible === null || eligible === undefined) ? "…" : fmt(eligible)}
          </div>
          <div className="mt-1 text-[10px] font-mono text-[var(--rl-text-secondary)] leading-none" data-testid={`${testid}-ratio`}>
            {isFinite(ratio) && ratio > 0 ? `${ratio >= 10 ? Math.round(ratio) : ratio.toFixed(1)}× target` : "—"}
          </div>
        </div>
      </div>
      {/* Health badge — sits BELOW the ring, not overlaid on it. */}
      <div
        className="mt-3 inline-flex items-center gap-2 px-2.5 py-1 border"
        style={{ borderColor: meta.color, color: meta.color }}
        data-testid={`${testid}-status`}
      >
        <span className="w-1.5 h-1.5" style={{ background: meta.color }} />
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold">{meta.label}</span>
      </div>
      {/* Threshold Whisper — subtle coaching tooltip. Appears on hover/focus
          of the ring, positioned below the health badge so it never blocks
          the ring itself. Copy is tier-specific. */}
      <div
        id={`${testid}-whisper`}
        role="tooltip"
        data-testid={`${testid}-whisper`}
        data-open={whisperOpen ? "true" : "false"}
        className={
          "mt-2 max-w-[240px] text-center text-[11px] leading-snug text-[var(--rl-text-secondary)] " +
          "transition-all duration-200 ease-out " +
          (whisperOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-1 pointer-events-none")
        }
        aria-hidden={!whisperOpen}
      >
        <span className="italic">{meta.whisper}</span>
      </div>
    </div>
  );
}
